import i18next from 'i18next';
import logger from '@widgets/logger';
import SentryManager from '@widgets/sentry-manager';
import { store } from 'store';
import { navigateTo } from '@helpers/navigation';
import { authActions, accountActions, alertActions } from '@actions';
import { accountConstants } from '@constants/account';
import { RoutePath } from '@constants/routes';
import { getActiveAccountId } from '@helpers/auth';

export const BASE_URL =
    import.meta.env.MODE !== 'development' ? `${window.location.origin}/` : import.meta.env.REACT_APP_API_URL;

const AuthRoutes = [
    RoutePath.Login,
    RoutePath.Signup,
    RoutePath.ResetPassword,
    RoutePath.ForgotPassword,
    RoutePath.Error,
    RoutePath.VerifyEmail,
];
const ServerCodes = {
    InternalServerError: 500,
    BadGateway: 502,
    GatewayTimeout: 504,
};
const ServerErrorCodes = [ServerCodes.InternalServerError, ServerCodes.BadGateway, ServerCodes.GatewayTimeout];
const SERVER_ERROR_HANDLED = { _serverError: true };

const REASON_DEPENDENT_ERROR_CODES = new Set(['google_validation_failed']);

const endpointPattern = path => path.replace(/\/(\d+|[0-9a-f-]{32,})(?=\/|$)/gi, '/{id}');

export class ApiResponseError extends Error {
    constructor(response) {
        const path = response.url ? new URL(response.url, window.location.origin).pathname : '';
        super(`${response.status} ${endpointPattern(path)}`);
        this.name = 'ApiResponseError';
        this.status = response.status;
        this.url = response.url;
    }
}

const reportResponse = (response, context) => {
    const error = new ApiResponseError(response);
    SentryManager.captureException(error, { context, status: error.status, url: error.url }, [
        'api-response',
        error.message,
    ]);
};

export const accountHeader = () => {
    const accountId = getActiveAccountId();
    return accountId != null ? { 'X-Account-Id': accountId } : {};
};

export const authHeader = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.token) {
        return { 'Content-Type': 'application/json', Authorization: 'Bearer ' + user.token, ...accountHeader() };
    } else {
        return {};
    }
};

const errorMiddleware = (response, data) => {
    if (response.status === 401) {
        const isAuthUrl = AuthRoutes.some(url => window.location.pathname.startsWith(url));
        if (!isAuthUrl) {
            store.dispatch(authActions.logout());
        }
    }
    const user = store.getState().auth.user;
    if (response.status === 402 && accountConstants.BILLING_EXPIRED === 0 && user) {
        const errorCode = data?.errorCode || data?.ErrorCode;
        const isAccountWide =
            !errorCode || errorCode === 'subscription_required' || errorCode === 'subscription_expired';
        if (!isAccountWide) {
            return;
        }
        accountConstants.BILLING_EXPIRED = 1;
        store.dispatch(accountActions.get());
        if (errorCode === 'subscription_required') {
            return;
        }
        const hasSettingsAccess =
            user.isOwner ||
            user.teams?.some(
                t => t.permissions.includes('settings:restricted') || t.permissions.includes('settings:full'),
            );
        if (!hasSettingsAccess) {
            reportResponse(response, 'Billing expired: 402');
            store.dispatch(authActions.logout());
        }
    }

    if (ServerErrorCodes.includes(response.status)) {
        // 502/504 are transient gateway blips (mostly deploy restarts) the backend
        // tracks via its own monitoring; only genuine 500s are worth a frontend event.
        if (response.status === ServerCodes.InternalServerError) {
            reportResponse(response, `Server error: ${response.status}`);
        }
        if (!user) {
            const currentPath = window.location.pathname + window.location.search;
            const errorPath =
                currentPath !== '/'
                    ? `${RoutePath.Error}?returnUrl=${encodeURIComponent(currentPath)}`
                    : RoutePath.Error;
            navigateTo(errorPath);
        } else {
            store.dispatch(alertActions.clearAll());
            store.dispatch(alertActions.error(i18next.t('apiErrors.an_unexpected_error_occurred')));
        }
    }
};

const handleInvalidActiveAccount = data => {
    if ((data?.errorCode || data?.ErrorCode) !== 'invalid_active_account') {
        return false;
    }
    localStorage.removeItem('activeAccountId');
    window.location.assign('/');
    return true;
};

export function handleResponse(response) {
    return response.text().then(text => {
        let data;
        try {
            data = text && JSON.parse(text);
        } catch (e) {
            logger.error('Failed to parse api response:', e);
        }
        if (!response.ok) {
            if (handleInvalidActiveAccount(data)) {
                return Promise.reject(SERVER_ERROR_HANDLED);
            }
            errorMiddleware(response, data);
            if (ServerErrorCodes.includes(response.status)) {
                return Promise.reject(SERVER_ERROR_HANDLED);
            }
            if (data?.errorCode || data?.ErrorCode) {
                return Promise.reject(data);
            }
            return Promise.reject(data?.message || response.statusText);
        }
        return data;
    });
}

const BEST_EFFORT_TIMEOUT_MS = 15000;

export const bestEffortRequest = (url, requestOptions) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), BEST_EFFORT_TIMEOUT_MS);
    return fetch(url, { ...requestOptions, signal: controller.signal })
        .then(response => (response.ok && response.status !== 204 ? response.json() : null))
        .finally(() => clearTimeout(timeout));
};

export function handleResponseBlob(response) {
    if (!response.ok) {
        return response.text().then(text => {
            let data;
            try {
                data = text && JSON.parse(text);
            } catch (e) {
                logger.error('Failed to parse api response:', e);
            }
            if (handleInvalidActiveAccount(data)) {
                return Promise.reject(SERVER_ERROR_HANDLED);
            }
            errorMiddleware(response, data);
            return Promise.reject("Couldn't download image!");
        });
    }
    return response.blob().then(blobObject => window.URL.createObjectURL(blobObject));
}

/**
 * @param {object} error - BE error object
 * @param {string} specificKey - keys like editFail, deleteFail, etc.
 * @param {object} i18nParams - error fields from BE
 * @param {boolean} withSentry - send error to Sentry
 */
export const translateApiError = (errorCode, fallback = '', params = {}) =>
    errorCode && i18next.exists(`apiErrors.${errorCode}`) ? i18next.t(`apiErrors.${errorCode}`, params) : fallback;

export const handleApiError = (error, specificKey, i18nParams = {}, sentryContext) => {
    if (error === SERVER_ERROR_HANDLED) return;
    const reasons = error?.hasStructuredReasons === false ? error?.reasons?.join(', ') : '';
    const params = { errors: error?.errors?.join(', '), reasons, ...(error?.details ?? {}), ...i18nParams };
    const carriesItsOwnDetail = reasons || !REASON_DEPENDENT_ERROR_CODES.has(error?.errorCode);
    const translated = carriesItsOwnDetail ? translateApiError(error?.errorCode, null, params) : null;
    if (translated) {
        store.dispatch(alertActions.error(translated));
    } else if (specificKey) {
        store.dispatch(alertActions.error(i18next.t(specificKey, params)));
    } else {
        store.dispatch(alertActions.error(i18next.t('apiErrors.something_wrong')));
    }
    if (sentryContext && import.meta.env.REACT_APP_STAGE === 'prod') {
        SentryManager.captureException(error, { context: sentryContext });
    }
};

// INFO: Request queue to avoid multiple requests at the same time
export class RequestQueue {
    constructor() {
        this.currentRequest = null;
    }

    async execute(requestFn) {
        if (this.currentRequest) {
            this.currentRequest.abort();
        }

        const controller = new AbortController();
        this.currentRequest = controller;

        try {
            const currentController = controller;
            const result = await requestFn(controller.signal);
            // Only return the result if this is still the current request
            if (this.currentRequest === currentController) {
                this.currentRequest = null;
                return result;
            }

            return Promise.reject({ aborted: true });
        } catch (error) {
            if (error.name === 'AbortError') {
                return Promise.reject({ aborted: true });
            }
            throw error;
        }
    }
}
